import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useHistory } from 'react-router';
import moment from 'moment';

import { selectBirthdayWish } from '@/store/selectors/profile';
import { getBirthdayWish, profileActions, saveBirthdayWish } from '@/store/actions/profile';

import useLanguage from '@/components/common/hooks/useLanguage';
import useInput from '@/components/common/hooks/useInput';
import CloseIcon from '@/components/common/icons/CloseIcon';
import Loader from '@/components/common/Loader';
import Button from '@/components/Button';
import TextArea from '@/components/TextArea';

// The page the Slack invite links to: what the signed-in person wants for their
// birthday. Whether it is still open is the server's call; this only follows it.
const BirthdayWish: React.FC = () => {
  const [{ birthdayWish: text }] = useLanguage();
  const dispatch = useDispatch();
  const history = useHistory();

  const data = useSelector(selectBirthdayWish);

  const [wish, setWish] = useInput('');
  const [isFilled, setIsFilled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useLayoutEffect(() => {
    dispatch(getBirthdayWish());

    return () => {
      dispatch(profileActions.cleanBirthdayWish());
    };
  }, []);

  // Filled once. The load is fresh-only, so there is no stored copy to be replaced by
  // a later one, and a save writes back exactly what is already in the box.
  useEffect(() => {
    if (data && !isFilled) {
      setWish(data.wish || '');
      setIsFilled(true);
    }
  }, [data]);

  const handleSave = async () => {
    setIsSaving(true);
    setNotice(null);
    const ok = await dispatch(saveBirthdayWish(wish)) as unknown as boolean;
    setIsSaving(false);
    setNotice(ok ? text.saved : text.save_failed);
  };

  const handleChange: typeof setWish = (event) => {
    setNotice(null);
    setWish(event);
  };

  const isUnchanged = (data?.wish || '') === wish.trim();

  const renderBody = () => {
    if (!data) return null;
    if (!data.birthday) return <Paragraph>{text.no_birthday}</Paragraph>;

    if (!data.isOpen) {
      return (
        <>
          <Paragraph>{text.intro(data.birthday)}</Paragraph>
          {data.wish ? <SavedWish>{data.wish}</SavedWish> : null}
          {data.opensOn && data.opensOn > moment().format('YYYY-MM-DD')
            ? <Muted>{text.opens(data.opensOn)}</Muted>
            : <Muted>{text.closed}</Muted>}
        </>
      );
    }

    return (
      <>
        <Wrapper>
          <Paragraph>{text.intro(data.birthday)}</Paragraph>
          <Muted>{text.hint(data.budget)}</Muted>
          <InputWrap>
            <TextArea
              name={text.label}
              placeholder={text.placeholder}
              descriptionValue={wish}
              setDescription={handleChange}
              height="160px"
            />
          </InputWrap>
          {data.lastDay && <Muted>{text.until(data.lastDay)}</Muted>}
          {notice && <Notice>{notice}</Notice>}
        </Wrapper>
        <ButtonWrap>
          <Button
            onClick={handleSave}
            isLoading={isSaving}
            disabledOnly={isUnchanged}
            shadow
            isFixed={false}
          >
            {text.save}
          </Button>
        </ButtonWrap>
      </>
    );
  };

  return (
    <>
      <Header>
        <IconWrap onClick={() => history.push('/me')}>
          <CloseIcon />
        </IconWrap>
        <Title>{text.title}</Title>
      </Header>
      {data ? <MainWrap>{renderBody()}</MainWrap> : <Loader />}
    </>
  );
};

const Header = styled.header`
  height: 72px;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.17);
  background-color: #ffffff;
  position: fixed;
  top: 0;
  left: 50%;
  max-width: 552px;
  width: 100%;
  transform: translateX(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
  padding: 0 16px;
`;

const IconWrap = styled.div`
  min-width: 24px;
  height: 24px;
  margin-right: 24px;
  cursor: pointer;
`;

const Title = styled.p`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const MainWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 100px 16px 16px;
  overflow-y: auto;
  flex-shrink: 0;
  background: white;
  justify-content: space-between;
`;

const Wrapper = styled.div`
  width: 100%;
`;

const Paragraph = styled.p`
  width: 100%;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.4;
  color: #21272e;
  margin: 0 0 12px;
`;

const Muted = styled.p`
  width: 100%;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  color: #6d7680;
  margin: 0 0 16px;
`;

const SavedWish = styled.p`
  width: 100%;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: #21272e;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  padding: 16px;
  border-radius: 8px;
  border: solid 1px #dae1e8;
  margin: 8px 0 16px;
`;

const InputWrap = styled.div`
  width: 100%;
  margin: 24px 0 12px;
`;

const Notice = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #3897ff;
  margin: 0;
`;

const ButtonWrap = styled.div`
  width: 100%;
  margin-top: 24px;
`;

export default BirthdayWish;
